import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { warehouseAPI } from '../../api/warehouses';
import { CreateWarehouseRequest } from '../../types/api';
import toast from 'react-hot-toast';

interface WarehouseFormData extends CreateWarehouseRequest {
  code?: string;
}

export const WarehouseForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [warehouseCode, setWarehouseCode] = useState<string>('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<WarehouseFormData>({
    defaultValues: {
      status: 'ACTIVE',
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      loadWarehouse(parseInt(id));
    }
  }, [id, isEdit]);

  const loadWarehouse = async (warehouseId: number) => {
    try {
      const warehouse = await warehouseAPI.getById(warehouseId);
      setWarehouseCode(warehouse.code);
      reset({
        name: warehouse.name,
        address: warehouse.address || '',
        contactPerson: warehouse.contactPerson || '',
        contactPhone: warehouse.contactPhone || '',
        status: warehouse.status || 'ACTIVE',
      });
    } catch (error) {
      toast.error(t('warehouses.loadError'));
      navigate('/warehouses');
    }
  };

  const onSubmit = async (data: WarehouseFormData) => {
    setLoading(true);
    try {
      if (isEdit && id) {
        await warehouseAPI.update(parseInt(id), data);
        toast.success(t('warehouses.updateSuccess'));
      } else {
        await warehouseAPI.create(data);
        toast.success(t('warehouses.createSuccess'));
      }
      navigate('/warehouses');
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">
        {isEdit ? t('warehouses.editWarehouse') : t('warehouses.addNew')}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="card space-y-4">
        {/* Warehouse Code (read-only for edit) */}
        {isEdit && warehouseCode && (
          <div>
            <label className="block text-sm font-medium mb-1">{t('warehouses.code')}</label>
            <input
              type="text"
              value={warehouseCode}
              disabled
              className="input-field bg-gray-100 font-mono"
            />
            <p className="text-xs text-gray-500 mt-1">{t('warehouses.codeAutoGenerated')}</p>
          </div>
        )}

        {/* Name */}
        <div>
          <label className="block text-sm font-medium mb-1">
            {t('warehouses.name')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register('name', { required: t('validation.required') })}
            className="input-field"
            placeholder={t('warehouses.namePlaceholder')}
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
          )}
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('warehouses.address')}</label>
          <textarea
            {...register('address')}
            className="input-field"
            rows={2}
            placeholder={t('warehouses.addressPlaceholder')}
          />
        </div>

        {/* Contact Person */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('warehouses.contactPerson')}</label>
          <input
            type="text"
            {...register('contactPerson')}
            className="input-field"
            placeholder={t('warehouses.contactPersonPlaceholder')}
          />
        </div>

        {/* Contact Phone */}
        <div>
          <label className="block text-sm font-medium mb-1">{t('warehouses.contactPhone')}</label>
          <input
            type="text"
            {...register('contactPhone')}
            className="input-field"
            placeholder={t('warehouses.contactPhonePlaceholder')}
          />
        </div>

        {/* Status (only for edit) */}
        {isEdit && (
          <div>
            <label className="block text-sm font-medium mb-1">{t('common.status')}</label>
            <select {...register('status')} className="input-field">
              <option value="ACTIVE">{t('common.active')}</option>
              <option value="INACTIVE">{t('common.inactive')}</option>
            </select>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => navigate('/warehouses')}
            className="btn-secondary"
          >
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? t('common.loading') : isEdit ? t('common.save') : t('common.add')}
          </button>
        </div>
      </form>
    </div>
  );
};
